/**
 * ChannelStrip — the five channels beside the inbox switcher (§10).
 *
 *   Emails · WhatsApp · Calls · Messages · Log
 *
 * Five, in that order, as the design draws them. It used to be eight tabs on a
 * row of their own: `Primary` has been folded into `Emails` (it was the same
 * list under a different name, and the design names it once), and `Tasks`,
 * `Tracking` and `Reminders` are gone from the inbox — Tasks has its own page,
 * and the other two were removed from the product by decision, not hidden.
 *
 * `Log` is an action, not a filter: it opens the log-communication dialog
 * rather than switching the list. It sits in the strip because the design puts
 * it there, and it is marked so that it never renders as the selected channel.
 */
import React from 'react';
import { CheckSquare, Mail, MessageCircle, MessageSquare, Phone, Plus } from 'lucide-react';

export const CHANNELS = [
    { key: 'emails', label: 'Emails', icon: Mail },
    { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
    { key: 'calls', label: 'Calls', icon: Phone },
    { key: 'messages', label: 'Messages', icon: MessageSquare },
];

export default function ChannelStrip({ active, onSelect, counts = {}, onLog }) {
    return (
        <div
            className="flex items-center gap-1 min-w-0 overflow-x-auto"
            role="tablist"
            aria-label="Channels"
            data-testid="inbox-channel-strip"
        >
            {CHANNELS.map((channel) => {
                const Icon = channel.icon;
                const selected = active === channel.key;
                const count = counts[channel.key] || 0;
                return (
                    <button
                        key={channel.key}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        onClick={() => onSelect(channel.key)}
                        data-testid={`inbox-channel-${channel.key}`}
                        className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-[9px]
                                    text-[13px] whitespace-nowrap transition-colors ${
                                        selected
                                            ? 'bg-[#EFEAFE] text-[#3B0DBE] font-semibold'
                                            : 'text-stone-600 hover:bg-[#F2F0EC]'
                                    }`}
                    >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {channel.label}
                        {count > 0 && (
                            <span
                                className="text-[11px] tabular-nums text-stone-400"
                                data-testid={`inbox-channel-count-${channel.key}`}
                            >
                                {count}
                            </span>
                        )}
                    </button>
                );
            })}

            {onLog && (
                <button
                    type="button"
                    onClick={onLog}
                    data-testid="inbox-channel-log"
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-[9px]
                               text-[13px] whitespace-nowrap text-stone-600 hover:bg-[#F2F0EC]"
                >
                    <Plus className="w-4 h-4 flex-shrink-0" />
                    Log
                </button>
            )}
        </div>
    );
}
